import { useEffect, useRef } from 'react';
import { EVENTS, publish, subscribe } from '@sendible/event-manager';
import { useUploadsContext } from '../../models/uploads/uploadsContext';
import { useBackgroundUploaderContext } from '../BackgroundUploader/context';
import { MediaFileType } from '../../models/medias/types';

const BULK_IMPORT_UPLOAD_CONCURRENCY_LIMIT = 4;

// A user-facing label for the tray row - the CSV row id isn't meaningful to a user, so
// show the file name portion of the URL instead (falling back to the full URL).
const deriveDisplayName = (url: string) => {
  try {
    const segment = new URL(url).pathname.split('/').filter(Boolean).pop();

    return segment ? decodeURIComponent(segment) : url;
  } catch {
    return url;
  }
};

// Uploads the bulk importer's CSV-row image/video URLs through the same pipeline the
// rest of the app uses instead of passing raw URLs to the API, and registers each one
// with the background uploader tray so the user gets the same upload feedback as
// everywhere else. Mounted unconditionally (see FrontendV2Components) rather than inside
// <MediaSystem/>, since a CSV import can kick off uploads without any compose box/media
// picker ever being open.
export const BulkImportUploadBridge = () => {
  const { uploadUrls } = useUploadsContext();
  const backgroundUploader = useBackgroundUploaderContext();
  const fileIdToRowId = useRef<Record<string, string>>({});
  // CANCEL_ALL_UPLOADS_AND_CLOSE_BU (published when the bulk importer closes - see
  // BulkImportView.js) already cancels every already-in-flight upload and clears the tray,
  // but says nothing to a run of uploadUrls still waiting on queued (not-yet-started) rows
  // under the concurrency limit below - without this, those would keep silently starting
  // one by one after the importer (and its tray) are long gone. Abort every such run (a
  // Set rather than a single ref, in case a second CSV is imported before the first
  // finishes uploading).
  const abortControllersRef = useRef<Set<AbortController>>(new Set());
  // abort() above only stops the worker queue (fromUrl.ts) from starting rows it hasn't
  // dispatched yet - it does nothing for the up-to-BULK_IMPORT_UPLOAD_CONCURRENCY_LIMIT rows
  // already mid-upload, which just run to completion regardless and would otherwise still
  // turn the row "Ready" once done. Tracks fileIds cancelled while still awaiting
  // onMediaCreated, so that if one fires anyway it's treated as an orphaned upload to clean
  // up (see onMediaCreated below) instead of a normal success.
  const cancelledFileIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const unsubscribeUploadUrls = subscribe(EVENTS.BULK_IMPORT_UPLOAD_URLS, async ({ rows }: { rows: { rowId: string; url: string }[] }) => {
      // useUploadUrls calls onUploadStarted once per file, synchronously, in the same
      // left-to-right order as `files` (before any of them actually await anything) -
      // safe to correlate back to a row via a plain FIFO queue.
      const rowIdQueue = rows.map(({ rowId }) => rowId);

      const abortController = new AbortController();

      abortControllersRef.current.add(abortController);

      try {
        // Every row registers with the background uploader immediately (so a CSV import
        // shows its whole batch in the tray straight away), but the uploader itself only
        // ever runs BULK_IMPORT_UPLOAD_CONCURRENCY_LIMIT of them at a time - the rest sit
        // queued until a slot frees, rather than hammering the upload API all at once.
        await uploadUrls({
          files: rows.map(({ url }) => ({ url, name: deriveDisplayName(url) })),
          concurrencyLimit: BULK_IMPORT_UPLOAD_CONCURRENCY_LIMIT,
          signal: abortController.signal,
          onUploadStarted: (file) => {
            const fileId = backgroundUploader.addUploadAndReturnId(file.name || file.url, MediaFileType.Unspecified);
            const rowId = rowIdQueue.shift();

            if (rowId) fileIdToRowId.current[fileId] = rowId;

            return fileId;
          },
          onMediaCreated: ({ fileId, mediaId, name }) => {
            const rowId = fileIdToRowId.current[fileId];

            delete fileIdToRowId.current[fileId];

            if (cancelledFileIdsRef.current.has(fileId)) {
              cancelledFileIdsRef.current.delete(fileId);
              // Cancelled while this row's upload was already too far along to stop - it
              // already got its own immediate "cancelled" feedback in the tray/grid (see
              // abortAllQueuedUploads below), so just clean up the now-orphaned media the
              // normal way (deletes it server-side, and suppresses the UPLOAD_READY it would
              // otherwise eventually get from polling).
              publish(EVENTS.UPLOAD_MEDIA_CANCELLED, mediaId);

              return;
            }

            backgroundUploader.updateFileMediaId({ fileId, mediaId });
            backgroundUploader.updateFileName({ fileId, name });

            if (rowId) {
              publish(EVENTS.BULK_IMPORT_MEDIA_CREATED, { rowId, mediaId });
            }
          },
        });
      } finally {
        abortControllersRef.current.delete(abortController);
      }
    });

    const abortAllQueuedUploads = () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
      abortControllersRef.current.clear();

      // Every row still waiting on onMediaCreated at this point - queued behind the
      // concurrency limit, or already mid-upload - needs its own "cancelled" feedback right
      // now, rather than leaving its skeleton pending indefinitely (or, worse, silently
      // turning Ready later): abort() above can't interrupt an upload already in progress, so
      // onMediaCreated may still fire for these later - see the cancelledFileIdsRef check there.
      Object.entries(fileIdToRowId.current).forEach(([fileId, rowId]) => {
        cancelledFileIdsRef.current.add(fileId);
        backgroundUploader.updateFileError({ fileId, errorCode: 'UploadCancelled' });
        publish(EVENTS.UPLOAD_FAILED, { fileId: rowId, error: 'UploadCancelled' });
      });
    };

    // CANCEL_ALL_UPLOADS_AND_CLOSE_BU (bulk importer/compose box closing) and
    // CANCEL_ALL_QUEUED_UPLOADS (the background uploader tray's own "Cancel all" button) both
    // need every still-queued row's upload stopped here - only this bridge knows about the
    // rows waiting behind BULK_IMPORT_UPLOAD_CONCURRENCY_LIMIT, since they don't have a media
    // record yet for the tray's own per-item cancellation to act on.
    const unsubscribeCancelAll = subscribe(EVENTS.CANCEL_ALL_UPLOADS_AND_CLOSE_BU, abortAllQueuedUploads);
    const unsubscribeCancelAllQueued = subscribe(EVENTS.CANCEL_ALL_QUEUED_UPLOADS, abortAllQueuedUploads);

    return () => {
      unsubscribeUploadUrls();
      unsubscribeCancelAll();
      unsubscribeCancelAllQueued();
    };
  }, [uploadUrls, backgroundUploader]);

  return null;
};
